import { useEffect, useState } from 'react';
import api from '@/services/axiosClient';
import type { PublicMaterial } from '@/types';

interface MaterialPreviewModalProps {
  material: PublicMaterial | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal dialog for quick look preview of marketing material files
 * Shows actual file content for images and PDFs, download link for other types
 * Uses blob URLs to handle authenticated file access
 */
export default function MaterialPreviewModal({ material, isOpen, onClose }: MaterialPreviewModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  // Fetch file with auth headers and create blob URL
  useEffect(() => {
    if (!isOpen || !material) {
      setError(null);
      setIsLoading(false);
      setFileUrl(null);
      return;
    }

    let blobUrl: string | null = null;
    
    const fetchFile = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch file with auth headers
        const response = await api.get(`/marketing-materials/${material.documentId}`, {
          responseType: 'blob'
        });
        
        // Create blob URL
        blobUrl = URL.createObjectURL(response.data);
        setFileUrl(blobUrl);
      } catch (err) {
        console.error('Failed to fetch file preview:', err);
        setError('Failed to load file preview. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFile();

    // Cleanup: revoke blob URL when component unmounts or material changes
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [isOpen, material]);
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !material) return null;

  const fileSizeMB = (material.fileSizeBytes / (1024 * 1024)).toFixed(2);
  const fileType = material.fileType.toLowerCase();
  
  // File types that can be previewed in browser
  const imageTypes = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];
  const pdfTypes = ['pdf'];

  const renderFilePreview = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-96 bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-3"></div>
            <p className="text-sm text-gray-600">Loading preview...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-96 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-center px-6">
            <span className="text-4xl mb-3 block">⚠️</span>
            <p className="text-sm text-red-900 font-medium">{error}</p>
          </div>
        </div>
      );
    }

    if (!fileUrl) {
      return null;
    }

    // Image preview
    if (imageTypes.includes(fileType)) {
      return (
        <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
          <img
            src={fileUrl}
            alt={material.headline}
            className="w-full h-auto max-h-[70vh] object-contain"
            onError={() => setError('Failed to load image preview')}
          />
        </div>
      );
    }

    // PDF preview
    if (pdfTypes.includes(fileType)) {
      return (
        <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden" style={{ height: '70vh' }}>
          <iframe
            src={fileUrl}
            className="w-full h-full"
            title={material.headline}
            onError={() => setError('Failed to load PDF preview')}
          />
        </div>
      );
    }

    // Unsupported file types - show download link
    return (
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-8 text-center">
        <div className="mb-4">
          <FileTypeIcon fileType={fileType} size="large" />
        </div>
        <h4 className="font-semibold text-gray-900 mb-2">Preview not available</h4>
        <p className="text-sm text-gray-600 mb-4">
          This file type cannot be previewed in the browser.
        </p>
        <a
          href={fileUrl}
          download={material.filename}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-md hover:bg-brand/90 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download File
        </a>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="preview-modal-title"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex-1 min-w-0 mr-4">
            <h2 id="preview-modal-title" className="text-lg font-semibold text-gray-900 truncate">
              {material.headline}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {material.filename} • {fileSizeMB} MB • {fileType.toUpperCase()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            aria-label="Close preview"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content - File Preview */}
        <div className="p-6">
          {renderFilePreview()}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Large file type icon for preview modal
 */
function FileTypeIcon({ fileType, size = 'normal' }: { fileType: string; size?: 'normal' | 'large' }) {
  const iconMap: Record<string, string> = {
    pdf: '📄',
    doc: '📝',
    docx: '📝',
    ppt: '📊',
    pptx: '📊',
    png: '🖼️',
    jpg: '🖼️',
    jpeg: '🖼️',
  };

  const sizeClass = size === 'large' ? 'text-8xl' : 'text-3xl';

  return (
    <div className={sizeClass}>
      {iconMap[fileType.toLowerCase()] || '📎'}
    </div>
  );
}
